import * as Sentry from '@sentry/react';

/**
 * Sound telemetry (#2041).
 *
 * The Release Center tones used to fail silently: `play()` rejections were
 * swallowed and late receipts were dropped without a trace, so "I heard Set up
 * but not GO" could only ever be answered with a guess. Every tone decision now
 * leaves a Sentry breadcrumb — so the surrounding events are attached to
 * whatever exception or report comes next — and a Plausible custom event, so the
 * played / skipped / failed rates are countable in aggregate.
 *
 * Both sinks are best-effort and non-throwing: telemetry must never be the
 * reason a tone does not play.
 */

export type SoundEvent = 'played' | 'skipped' | 'failed';

export type SoundFailureReason =
  /** The browser's autoplay policy rejected `play()` (no user gesture yet). */
  | 'autoplay-blocked'
  /** The file could not be fetched from the bundle or the CDN. */
  | 'network'
  /** The file was fetched but `decodeAudioData` rejected it. */
  | 'decode'
  | 'unknown';

export interface SoundReport {
  /** Which sound (`go`, `setup`, `pop`, ...). */
  name: string;
  /** For `failed`, why; for `skipped`, the gate outcome that skipped it. */
  reason?: SoundFailureReason | string;
  /** Which half of the pipeline failed. */
  stage?: 'decode' | 'play';
  /** Was this receipt delivered late (reconnect catch-up) rather than live? */
  late?: boolean;
  /** How far behind its `createdAt` the receipt was handled, in ms. */
  ageMs?: number;
  /** Had a user gesture unlocked audio at the time? */
  unlocked?: boolean;
}

export function reportSound(event: SoundEvent, report: SoundReport): void {
  const data: Record<string, string | number | boolean> = { name: report.name };

  if (report.reason) {
    data.reason = report.reason;
  }
  if (report.stage) {
    data.stage = report.stage;
  }
  if (typeof report.late === 'boolean') {
    data.late = report.late;
  }
  if (typeof report.ageMs === 'number') {
    data.ageMs = Math.round(report.ageMs);
  }
  if (typeof report.unlocked === 'boolean') {
    data.unlocked = report.unlocked;
  }

  try {
    Sentry.addBreadcrumb({
      category: 'sound',
      type: 'user',
      level: event === 'failed' ? 'warning' : 'info',
      message: `sound/${event} ${report.name}${report.reason ? ` (${report.reason})` : ''}`,
      data,
    });
  } catch {
    //
  }

  try {
    // Plausible is first-party, cookieless and present on every page in prod
    // (see pages/+Head.tsx); absent in dev, hence the optional call.
    const { plausible } = window as unknown as {
      plausible?: (name: string, options?: { props?: Record<string, unknown> }) => unknown;
    };

    plausible?.(`sound/${event}`, { props: data });
  } catch {
    //
  }
}
